import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { AnimatePresence, m } from 'motion/react';
import { AlertTriangle, Archive, ArchiveRestore, FolderGit2, ListTodo, Plus, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  BOARD_COLUMNS,
  boardTabs,
  columnCards,
  columnCount,
  NO_PROJECT,
  PRIORITY_BARS,
  PRIORITY_TONE,
  resolveTab,
  statusTone,
  TERMINAL_STATUSES,
  type BoardTab,
} from './board';
import { projects as projectsApi } from '../../api/projects';
import { tasks, type Task, type TaskCard } from '../../api/tasks';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Modal } from '../../components/Modal';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { PageHeader } from '../../components/Surface';
import { useFormat } from '../../i18n/format';
import { useI18n, useT } from '../../i18n/provider';
import type { Key } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useCompanyId, useCompanySlug } from '../company/useCompany';

const TASKS_ROUTE = '/authenticated/c/$companySlug/tasks';

/**
 * Los tableros de `13 §3.6`: uno por proyecto, en pestañas.
 *
 * Hasta el 2026-09-11 era un solo kanban con un desplegable de proyecto que
 * por defecto mezclaba todos. Con tres proyectos vivos la columna «En marcha»
 * intercalaba tarjetas de tiendas distintas y la de «Terminadas» era un
 * montón de nadie. Ahora cada proyecto tiene su tablero y la pestaña dice de
 * cuál se está hablando; las tareas sin proyecto tienen el suyo, «Sin
 * proyecto», que solo aparece si hay alguna. Lo que cruza los proyectos es
 * el **buscador**: por título o número, en todos y también en el archivo,
 * porque «¿dónde estaba la del IBAN?» es una pregunta a la empresa entera.
 *
 * La pestaña y la búsqueda van en la URL —`?project=`, `?q=`— y la última
 * pestaña se recuerda por empresa: «Tareas» en la barra lateral vuelve al
 * tablero que se estaba mirando, no al primero de la lista.
 *
 * Una tarea bloqueada en un proyecto que no se está mirando sería justo el
 * «esconderse detrás del camino feliz» que este tablero promete no permitir,
 * así que cada pestaña lleva su cuenta de bloqueadas a la vista. Es una
 * consulta más —las bloqueadas de la empresa, que son pocas por definición—
 * y sondea al mismo ritmo que el tablero.
 *
 * El tablero de cada pestaña sondea en vez de suscribirse, por la misma razón
 * que la lista de ejecuciones: una tarea que ha cambiado de columna no tiene
 * ningún tema que esta pantalla ya esté vigilando, así que «¿se ha movido
 * algo?» es una pregunta que solo un sondeo puede responder. Diez segundos
 * bastan: las transiciones del orquestador están a minutos unas de otras.
 *
 * Una desviación consciente del boceto: sin arrastrar y soltar. Las
 * transiciones que expone la API son acciones con nombre, con sus propias
 * confirmaciones y motivos (cancelar exige uno), y un soltar que eligiera una
 * de ellas en silencio escondería justo lo que las pantallas de acción existen
 * para decir. La tarjeta abre el detalle, y el detalle lleva los botones.
 */
export function TasksScreen() {
  const t = useT();
  const companyId = useCompanyId();
  const companySlug = useCompanySlug();
  const navigate = useNavigate();
  const { project: requested, q = '' } = useSearch({ from: TASKS_ROUTE });
  const [showArchived, setShowArchived] = useState(false);

  const projectList = useQuery({
    queryKey: ['projects', companyId],
    queryFn: () => projectsApi.list(companyId),
    enabled: companyId !== '',
  });

  // ¿Hay tareas sin proyecto? Una fila basta para saberlo, y la pestaña solo
  // existe si la respuesta es sí: una pestaña «Sin proyecto» siempre vacía
  // sería una invitación a preguntarse qué falla.
  const unassigned = useQuery({
    queryKey: ['tasks', 'unassigned-probe', companyId],
    queryFn: () => tasks.list(companyId, { project_id: NO_PROJECT, limit: '1' }),
    enabled: companyId !== '',
  });

  const blocked = useQuery({
    queryKey: ['tasks', 'blocked', companyId],
    queryFn: () => tasks.list(companyId, { status: 'blocked', archived: 'false', limit: '100' }),
    enabled: companyId !== '',
    refetchInterval: 10_000,
    staleTime: 0,
  });

  const tabs = boardTabs(projectList.data?.data ?? [], t('tasks.noProject'), (unassigned.data?.data.length ?? 0) > 0);
  const tab = resolveTab(tabs, requested, rememberedTab(companyId));
  const searching = q !== '';

  useEffect(() => {
    if (tab !== null) rememberTab(companyId, tab.key);
  }, [companyId, tab]);

  const blockedByTab = countBlocked(blocked.data?.data ?? []);

  const setQuery = (value: string) =>
    void navigate({
      to: '/c/$companySlug/tasks',
      params: { companySlug },
      search: {
        ...(requested !== undefined ? { project: requested } : {}),
        ...(value !== '' ? { q: value } : {}),
      },
      replace: true,
    });

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('tasks.title')}
        description={t('tasks.intro')}
        actions={
          <>
            <SearchBox value={q} onChange={setQuery} />

            {/*
              Un conmutador y no una ruta: el archivo es la trastienda del
              tablero, no una pantalla con vida propia, y sigue a la pestaña
              que esté puesta.
            */}
            {!searching ? (
              <Button
                variant="secondary"
                icon={showArchived ? <ArchiveRestore /> : <Archive />}
                onClick={() => setShowArchived((current) => !current)}
              >
                {showArchived ? t('tasks.backToBoard') : t('tasks.showArchived')}
              </Button>
            ) : null}

            <Link
              to="/c/$companySlug/tasks/new"
              params={{ companySlug }}
              search={tab !== null && tab.key !== NO_PROJECT ? { project: tab.key } : {}}
            >
              <Button icon={<Plus />}>{t('tasks.newTask')}</Button>
            </Link>
          </>
        }
      />

      {searching ? (
        <SearchResults companyId={companyId} companySlug={companySlug} query={q} onClear={() => setQuery('')} />
      ) : projectList.isPending || unassigned.isPending ? (
        <SkeletonRows rows={3} />
      ) : projectList.error !== null ? (
        <ErrorBanner error={projectList.error} onRetry={() => void projectList.refetch()} />
      ) : unassigned.error !== null ? (
        <ErrorBanner error={unassigned.error} onRetry={() => void unassigned.refetch()} />
      ) : tab === null ? (
        <EmptyState
          icon={<ListTodo />}
          title={t('tasks.noBoards')}
          description={t('tasks.noBoardsHint')}
          action={
            <Link to="/c/$companySlug/projects" params={{ companySlug }} className="text-sm font-semibold text-primary">
              {t('company.tabs.projects')}
            </Link>
          }
        />
      ) : (
        <>
          <TabStrip tabs={tabs} current={tab} blockedByTab={blockedByTab} companySlug={companySlug} />

          {showArchived ? (
            <ArchivedTasks companyId={companyId} projectId={tab.projectId} companySlug={companySlug} />
          ) : (
            <ProjectBoard key={tab.key} companyId={companyId} companySlug={companySlug} tab={tab} />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Cuántas bloqueadas tiene cada pestaña, por el id de proyecto de la tarea —
 * `NO_PROJECT` para las que no tienen—. Es lo que la fila de pestañas pinta
 * al lado de cada nombre.
 */
function countBlocked(rows: Task[]): Map<string, number> {
  const counts = new Map<string, number>();

  for (const row of rows) {
    const key = row.project?.id ?? NO_PROJECT;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return counts;
}

const TAB_MEMORY = 'absor.tasks.tab.';

function rememberedTab(companyId: string): string | null {
  try {
    return localStorage.getItem(TAB_MEMORY + companyId);
  } catch {
    return null;
  }
}

function rememberTab(companyId: string, key: string): void {
  try {
    localStorage.setItem(TAB_MEMORY + companyId, key);
  } catch {
    // Sin almacenamiento (modo privado, cuota) la pestaña simplemente no se
    // recuerda; el tablero funciona igual.
  }
}

/**
 * La fila de pestañas: enlaces, no botones, para que una pestaña se pueda
 * abrir en otra ventana y copiar como URL. Se desplaza en horizontal antes
 * que envolverse: una empresa con doce proyectos sigue teniendo una fila.
 */
function TabStrip({
  tabs,
  current,
  blockedByTab,
  companySlug,
}: {
  tabs: BoardTab[];
  current: BoardTab;
  blockedByTab: Map<string, number>;
  companySlug: string;
}) {
  const t = useT();
  const { plural } = useI18n();

  return (
    <nav
      aria-label={t('tasks.boards')}
      className="-mx-4 flex gap-1 overflow-x-auto border-b border-line px-4 text-sm sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10"
    >
      {tabs.map((tab) => {
        const active = tab.key === current.key;
        const blockedCount = blockedByTab.get(tab.projectId) ?? 0;

        return (
          <Link
            key={tab.key}
            to="/c/$companySlug/tasks"
            params={{ companySlug }}
            search={{ project: tab.key }}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'relative flex shrink-0 items-center gap-1.5 px-3 pb-2.5 whitespace-nowrap transition-colors duration-fast',
              active ? 'font-semibold text-ink' : 'text-ink-soft hover:text-ink',
              tab.key === NO_PROJECT && !active && 'italic',
            )}
          >
            {tab.name}
            {blockedCount > 0 ? (
              <span
                title={plural('tasks.tabBlocked', blockedCount)}
                aria-label={plural('tasks.tabBlocked', blockedCount)}
                className="inline-flex min-w-4 items-center justify-center rounded-full bg-warning-pale px-1 text-[0.62rem] font-semibold text-warning tabular-nums"
              >
                {blockedCount}
              </span>
            ) : null}
            {active ? (
              <m.span
                layoutId="tasks-tab"
                className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary"
                transition={{ type: 'spring', stiffness: 520, damping: 42 }}
              />
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * El buscador de la cabecera. Escribe en la URL con un cuarto de segundo de
 * calma —cada tecla no es una petición— y con `replace`, para que «atrás»
 * salga de la búsqueda entera y no la deshaga letra a letra.
 */
function SearchBox({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const t = useT();
  const [draft, setDraft] = useState(value);

  // La URL manda: «atrás», o un enlace con `?q=`, tienen que verse en la caja.
  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    if (draft === value) return undefined;

    const timer = setTimeout(() => onChange(draft.trim()), 250);

    return () => clearTimeout(timer);
  }, [draft, value, onChange]);

  return (
    <label className="relative flex items-center">
      <span className="sr-only">{t('tasks.search')}</span>
      <Search className="pointer-events-none absolute left-2.5 size-4 text-ink-faint" aria-hidden="true" />
      <input
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={t('tasks.search')}
        title={t('tasks.searchHint')}
        className={cn(fieldStyles, 'w-56 border-line py-1.5 pr-8 pl-8 text-sm sm:w-72')}
      />
      {draft !== '' ? (
        <button
          type="button"
          onClick={() => {
            setDraft('');
            onChange('');
          }}
          aria-label={t('tasks.clearSearch')}
          className="absolute right-1.5 grid size-6 place-items-center rounded-sm text-ink-faint transition-colors duration-fast hover:bg-muted hover:text-ink"
        >
          <X className="size-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </label>
  );
}

/**
 * Los resultados del buscador: todos los proyectos y también el archivo,
 * porque quien busca por número quiere encontrar la tarea esté donde esté.
 * Cada tarjeta dice su proyecto, que es lo que las pestañas dan por sabido y
 * aquí no se puede dar.
 */
function SearchResults({
  companyId,
  companySlug,
  query,
  onClear,
}: {
  companyId: string;
  companySlug: string;
  query: string;
  onClear: () => void;
}) {
  const t = useT();
  const { plural } = useI18n();
  const results = useQuery({
    queryKey: ['tasks', 'search', companyId, query],
    queryFn: () => tasks.list(companyId, { q: query, limit: '50' }),
    enabled: companyId !== '',
    staleTime: 0,
  });

  if (results.isPending) return <SkeletonRows rows={3} />;
  if (results.error !== null) return <ErrorBanner error={results.error} onRetry={() => void results.refetch()} />;

  const rows = results.data?.data ?? [];

  return (
    <div className="space-y-4">
      <p role="status" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-soft">
        <span>{plural('tasks.searchResults', rows.length, { query })}</span>
        {results.data?.meta.has_more ? <span className="text-ink-faint">{t('tasks.searchMore')}</span> : null}
        <button type="button" onClick={onClear} className="font-semibold text-primary hover:underline">
          {t('tasks.clearSearch')}
        </button>
      </p>

      {rows.length > 0 ? (
        <ul className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {rows.map((row) => (
            <li key={row.id}>
              <BoardCard card={row} companySlug={companySlug} project={row.project} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** El kanban de una pestaña: las siete columnas de `board.ts` para un proyecto. */
function ProjectBoard({ companyId, companySlug, tab }: { companyId: string; companySlug: string; tab: BoardTab }) {
  const t = useT();
  const format = useFormat();

  const board = useQuery({
    queryKey: ['tasks', 'board', companyId, tab.projectId],
    queryFn: () => tasks.board(companyId, tab.projectId),
    enabled: companyId !== '',
    refetchInterval: 10_000,
    // Una superficie que sondea no puede servir el tablero de la visita
    // anterior durante un minuto tras remontarse: el `staleTime` global existe
    // para lecturas, no para pantallas cuyo sentido entero es qué acaba de
    // cambiar.
    staleTime: 0,
  });

  if (board.isPending) return <SkeletonRows rows={3} />;
  if (board.error !== null) return <ErrorBanner error={board.error} onRetry={() => void board.refetch()} />;
  if (board.data === undefined) return null;

  return (
    <>
      {(board.data.counts['blocked'] ?? 0) > 0 ? (
        <BlockedBanner
          cards={board.data.columns['blocked'] ?? []}
          total={board.data.counts['blocked'] ?? 0}
          companySlug={companySlug}
        />
      ) : null}
      <div className="-mx-4 flex gap-4 overflow-x-auto px-4 pb-4 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10">
        {BOARD_COLUMNS.map((column) => {
          const cards = columnCards(board.data, column.statuses);
          const total = columnCount(board.data, column.statuses);

          return (
            <section key={column.key} className="w-68 shrink-0 space-y-2.5">
              <h3 className="flex items-center justify-between px-1 text-sm font-semibold text-ink">
                {t(`tasks.columns.${column.key}` as Key)}
                <span className="flex items-center gap-1 text-xs text-ink-faint tabular-nums">
                  {cards.length < total
                    ? t('tasks.ofTotal', { shown: cards.length, total })
                    : format.number(total)}
                  {/*
                    El gesto en masa vive donde está el montón: la columna
                    capa sus tarjetas, así que el barrido lo hace el servidor
                    sobre el total del contador, no sobre lo visible.
                  */}
                  {column.key === 'finished' && total > 0 ? (
                    <ArchiveFinishedButton companyId={companyId} projectId={tab.projectId} scope={tab.name} count={total} />
                  ) : null}
                </span>
              </h3>

              {cards.length === 0 ? (
                <p className="rounded-md border border-dashed border-line px-3 py-6 text-center text-xs text-ink-faint">
                  {t('tasks.emptyColumn')}
                </p>
              ) : (
                <ul className="space-y-2">
                  <AnimatePresence initial={false} mode="popLayout">
                    {cards.map((card) => (
                      <m.li
                        key={card.id}
                        layout
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 34 }}
                      >
                        <BoardCard card={card} companySlug={companySlug} />
                      </m.li>
                    ))}
                  </AnimatePresence>
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
}

/**
 * La trastienda del tablero: las terminadas que alguien quitó de en medio.
 *
 * Una lista y no siete columnas, porque aquí no hay flujo que seguir: todo lo
 * archivado es terminal por definición (la API rechaza archivar una viva con un
 * 409). Sin sondeo: nada mueve una tarea al archivo salvo un botón, y los dos
 * botones que lo hacen invalidan `['tasks']` al terminar. Acotado a la pestaña,
 * como el tablero: el archivo de Web Shop es el de Web Shop.
 */
function ArchivedTasks({
  companyId,
  projectId,
  companySlug,
}: {
  companyId: string;
  projectId: string;
  companySlug: string;
}) {
  const t = useT();
  const archived = useQuery({
    queryKey: ['tasks', 'archived', companyId, projectId],
    queryFn: () => tasks.list(companyId, { archived: 'true', project_id: projectId }),
    enabled: companyId !== '',
    staleTime: 0,
  });

  if (archived.isPending) return <SkeletonRows rows={3} />;
  if (archived.error !== null) return <ErrorBanner error={archived.error} onRetry={() => void archived.refetch()} />;

  const rows = archived.data?.data ?? [];

  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-line px-4 py-10 text-center text-sm text-ink-faint">
        {t('tasks.archivedEmpty')}
      </p>
    );
  }

  return (
    <ul className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      <AnimatePresence initial={false} mode="popLayout">
        {rows.map((card) => (
          <m.li
            key={card.id}
            layout
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 400, damping: 34 }}
          >
            <BoardCard card={card} companySlug={companySlug} />
          </m.li>
        ))}
      </AnimatePresence>
    </ul>
  );
}

/**
 * Las bloqueadas, delante de todo. La columna «Bloqueadas» existe, pero es la
 * sexta de siete en una fila con desplazamiento horizontal: en un portátil
 * queda fuera de pantalla, que es exactamente el «esconderse detrás del camino
 * feliz» que el tablero promete no permitir. Una tarea bloqueada espera a una
 * persona, así que se anuncia donde la persona ya está mirando.
 */
function BlockedBanner({ cards, total, companySlug }: { cards: TaskCard[]; total: number; companySlug: string }) {
  const { plural } = useI18n();

  return (
    <m.div
      role="status"
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-start gap-3 rounded-md border border-warning-line bg-warning-pale px-4 py-3 text-sm text-ink"
    >
      <AlertTriangle className="mt-px size-4 shrink-0 text-warning" aria-hidden="true" />
      <span>
        <strong className="font-semibold">{plural('tasks.blockedBanner', total)}</strong>{' '}
        {cards.map((card, index) => (
          <span key={card.id}>
            {index > 0 ? ' · ' : ''}
            <Link
              to="/c/$companySlug/tasks/$taskNumber"
              params={{ companySlug, taskNumber: String(card.number) }}
              className="font-medium text-primary hover:underline"
            >
              #{card.number} {card.title}
            </Link>
          </span>
        ))}
      </span>
    </m.div>
  );
}

/** La barra de prioridad: cuatro escalones que se distinguen sin leer. */
function PriorityBars({ priority }: { priority: TaskCard['priority'] }) {
  const filled = PRIORITY_BARS[priority];
  const tone = { neutral: 'bg-ink-faint', info: 'bg-info', danger: 'bg-danger', primary: 'bg-primary', success: 'bg-success', warning: 'bg-warning' }[
    PRIORITY_TONE[priority]
  ];

  return (
    <span className="flex items-end gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4].map((step) => (
        <span
          key={step}
          className={cn('w-0.5 rounded-full', step <= filled ? tone : 'bg-line', ['h-1.5', 'h-2', 'h-2.5', 'h-3'][step - 1])}
        />
      ))}
    </span>
  );
}

/**
 * La tarjeta. `project` solo viaja desde el buscador: en el tablero la pestaña
 * ya lo dice, y repetirlo en cada tarjeta sería ruido; entre resultados de
 * proyectos distintos es la primera línea que se lee.
 */
function BoardCard({
  card,
  companySlug,
  project,
}: {
  card: TaskCard;
  companySlug: string;
  project?: { name: string } | null;
}) {
  const t = useT();
  const format = useFormat();

  return (
    <div className="relative">
      <Link
        to="/c/$companySlug/tasks/$taskNumber"
        params={{ companySlug, taskNumber: String(card.number) }}
        className="block rounded-md border border-line bg-raised p-3.5 text-sm shadow-xs transition-[border-color,box-shadow,transform] duration-fast hover:-translate-y-px hover:border-primary/45 hover:shadow-md"
      >
        {project !== undefined ? (
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-ink-soft">
            <FolderGit2 className="size-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
            <span className={cn('truncate', project === null && 'italic text-ink-faint')}>
              {project === null ? t('tasks.noProject') : project.name}
            </span>
            {card.archived_at !== null ? (
              <Badge tone="neutral" size="sm" className="ml-auto">
                {t('tasks.archivedBadge')}
              </Badge>
            ) : null}
          </p>
        ) : null}
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-ink-faint">#{card.number}</span>
          <span className="text-xs text-ink-soft">{t(`tasks.type.${card.type}` as Key)}</span>
          <Badge tone={statusTone(card.status)} size="sm" className="ml-auto">
            {t(`tasks.status.${card.status}` as Key)}
          </Badge>
        </div>

        <p className="mt-2 leading-snug font-medium text-ink">{card.title}</p>

        <div className="mt-3 flex items-center gap-2 text-xs text-ink-soft">
          <PriorityBars priority={card.priority} />
          <span>{t(`tasks.priority.${card.priority}` as Key)}</span>
          <span className="text-ink-faint">
            · {t('tasks.risk', { level: t(`tasks.riskLevel.${card.risk_level}` as Key) })}
          </span>
          {card.cost_micro_usd > 0 ? (
            <span className="ml-auto font-medium tabular-nums">{format.money(card.cost_micro_usd)}</span>
          ) : null}
        </div>

        {/* El hueco de la esquina es del botón de archivar, que vive fuera del enlace. */}
        <p className={cn('mt-1.5 text-xs text-ink-faint', TERMINAL_STATUSES.has(card.status) && 'pr-7')}>
          {card.assignee ? card.assignee.name : t('tasks.unassigned')}
        </p>
      </Link>

      {TERMINAL_STATUSES.has(card.status) ? <ShelfButton card={card} /> : null}
    </div>
  );
}

/**
 * El barrido de la columna «Terminadas»: `POST /companies/{id}/tasks/archive-finished`.
 *
 * Con confirmación, y no porque destruya nada —no hay estado que cambie— sino
 * porque no hay desarchivado en lote: deshacer un barrido de 140 son 140
 * tarjetas. El diálogo dice el total del contador, que es lo que de verdad se
 * archiva; la columna capa sus tarjetas y por eso los ids no viajan — el
 * servidor barre el conjunto entero, acotado al tablero de la pestaña.
 *
 * Exportado para probarse solo: el tablero que lo rodea necesita media API
 * simulada y este botón solo necesita saber contar.
 */
export function ArchiveFinishedButton({
  companyId,
  projectId,
  scope,
  count,
}: {
  companyId: string;
  /** El de la pestaña: un uuid o `none`. Siempre hay uno, porque ya no hay tablero de todos. */
  projectId: string;
  /** El nombre de la pestaña, para que el diálogo diga de qué tablero habla. */
  scope: string;
  count: number;
}) {
  const t = useT();
  const { plural } = useI18n();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const sweep = useMutation({
    mutationFn: () => tasks.archiveFinished(companyId, projectId),
    onSuccess: () => {
      setOpen(false);
      // `['tasks']` cubre el tablero y el archivo: las tarjetas salen de uno
      // y aparecen en el otro con el mismo gesto que el botón de la esquina.
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const label = plural('tasks.archiveFinished', count);

  return (
    <>
      <button
        type="button"
        title={label}
        aria-label={label}
        onClick={() => setOpen(true)}
        className="-my-1 grid size-6 place-items-center rounded-sm text-ink-faint transition-colors duration-fast hover:bg-muted hover:text-ink"
      >
        <Archive className="size-3.5" aria-hidden="true" />
      </button>

      <Modal
        open={open}
        size="sm"
        title={t('tasks.archiveFinishedTitle')}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button icon={<Archive />} busy={sweep.isPending} onClick={() => sweep.mutate()}>
              {label}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-soft">
          {plural('tasks.archiveFinishedBody', count)} {t('tasks.archiveFinishedProject', { project: scope })}
        </p>
        {sweep.error !== null ? (
          <div className="mt-3">
            <ErrorBanner error={sweep.error} />
          </div>
        ) : null}
      </Modal>
    </>
  );
}

/**
 * Archivar desde la tarjeta, que es donde estorba: la columna «Terminadas»
 * acumula para siempre y este es el gesto que la vacía. Hermano del enlace, no
 * hijo —un botón dentro de un `<a>` no es HTML y el clic navegaría—, colocado
 * encima por posición absoluta en la esquina que la tarjeta le deja libre.
 */
function ShelfButton({ card }: { card: TaskCard }) {
  const t = useT();
  const queryClient = useQueryClient();
  const archived = card.archived_at !== null;

  const act = useMutation({
    mutationFn: () => (archived ? tasks.unarchive(card.id) : tasks.archive(card.id)),
    // `['tasks']` cubre el tablero y el archivo a la vez: la tarjeta sale de
    // una lista y entra en la otra en el mismo gesto.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const label = archived ? t('tasks.unarchive', { number: card.number }) : t('tasks.archive', { number: card.number });
  const Icon = archived ? ArchiveRestore : Archive;

  return (
    <>
      <button
        type="button"
        title={label}
        aria-label={label}
        disabled={act.isPending}
        onClick={() => act.mutate()}
        className="absolute right-2 bottom-2 grid size-6 place-items-center rounded-sm text-ink-faint transition-colors duration-fast hover:bg-muted hover:text-ink disabled:opacity-50"
      >
        <Icon className="size-3.5" aria-hidden="true" />
      </button>
      {act.error !== null ? (
        <p role="alert" className="mt-1 text-xs text-danger">
          {act.error.message}
        </p>
      ) : null}
    </>
  );
}
